import { Auth } from "@/api";
import { AuthState, RootState, User } from "@/store/types";
import Cookies from "js-cookie";
import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
const USER_STORAGE_KEY = "lms_user";

// The token is kept in a cookie so axios can read it, but nothing used to
// restore the user, so a page reload left the app with no permissions and every
// permission-gated control hidden. Read the cached user back synchronously so
// `created()` hooks see it, then refresh it from the API on boot.
function readStoredUser(): User | null {
  try {
    const stored = window.localStorage.getItem(USER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
}

function writeStoredUser(user: User | null): void {
  try {
    if (user) {
      window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(USER_STORAGE_KEY);
    }
  } catch (e) {
    // Storage is unavailable (private mode, blocked site data) - the app still
    // works, it just refetches the user on the next boot.
  }
}

const state: AuthState = {
  accessToken: Cookies.get("access_token") || "",
  isCorrect: true,
  user: Cookies.get("access_token") ? readStoredUser() : null
};

const namespaced = true;

const actions: ActionTree<AuthState, RootState> = {
  async loginRequest(
    { commit },
    { email, password }: { email: string; password: string }
  ) {
    try {
      const res = await Auth.login(email, password);
      commit("setAccessToken", res.access_token);
      commit("setUser", {
        id: res.id,
        email: res.email,
        permissions: res.permissions
      });
    } catch (e) {
      const err = e as any;
      throw err.response.data;
    }
  },
  async fetchUser({ commit, state }) {
    if (!state.accessToken) {
      return;
    }
    try {
      const user = await Auth.me();
      commit("setUser", {
        id: user.id,
        email: user.email,
        permissions: user.permissions
      });
    } catch (e) {
      // Leave the cached user in place: a transient failure should not log the
      // session out, and a rejected token is handled by the refresh interceptor.
    }
  },
  async authLogout({ commit, state }) {
    if (state.user) {
      await Auth.logout(state.user.email);
      Cookies.remove("access_token");
      commit("setAccessToken", "");
      commit("setUser", null);
    }
  }
};

const mutations: MutationTree<AuthState> = {
  setAccessToken(state: AuthState, token: string): void {
    state.accessToken = token;
    Cookies.set("access_token", token, { expires: 365 });
  },
  setUser(state: AuthState, user: User) {
    state.user = user;
    writeStoredUser(user);
  },
  setIsCorrect(state: AuthState, flag: boolean): void {
    state.isCorrect = flag;
  }
};

const getters: GetterTree<AuthState, RootState> = {
  loggedIn(state: AuthState) {
    return !!state.accessToken;
  },
  getIsCorrect(state: AuthState): boolean {
    return state.isCorrect;
  },
  getUser(state: AuthState): object | null {
    return state.user;
  }
};

const auth: Module<AuthState, RootState> = {
  namespaced,
  state,
  mutations,
  getters,
  actions
};

export default auth;
